import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Outlet, useSearchParams } from 'react-router-dom'

import s from './Home.module.scss'

import AdSpotCard from './components/AdSpotCard'

import { Publisher } from 'types/publisher'

import { METAVERSE_IMAGE_BY_NAME_FILTER } from 'constants/MetaverseIcons'
import { SORT_ICON_BY_VALUE, SORT_OPTIONS } from 'constants/sortOptions'

import { useCreateMetaOpts } from 'hooks/useCreateMetaOpts'
import { usePagination } from 'hooks/usePagination'

import { adSpotsApi } from 'api/adSpots-api'
import { publishersApi } from 'api/publishers-api'

import { GlobeIcon } from 'assets/icons/GlobeIcon'
import { SelectIcon } from 'assets/icons/SelectIcon'
import metamaskIcon from 'assets/svg/icons-crypto-metamask.svg'
import publisher from 'assets/svg/icons-publisher.svg'

import { SelectControlled } from 'components/Controls/Select/SelectControlled'
import { SelectAsyncControlled } from 'components/Controls/SelectAsync/SelectAsyncControlled'
import { BurgerWrapper } from 'components/UI/BurgerWrapper/BurgerWrapper'
import Header from 'components/UI/Header'
import { NoContent } from 'components/UI/NoContent/NoContent'
import PageTitle from 'components/UI/PageTitle'
import Search from 'components/UI/Search'

const usersDefault = { value: '', label: 'All Landowners' }

export const Home = () => {
  const [searchParams] = useSearchParams()

  const { filterMetaverseOptions } = useCreateMetaOpts()

  const { control, setValue } = useForm({
    mode: 'onBlur',
    defaultValues: {
      usersSelect: usersDefault,
      metaverseSelect: filterMetaverseOptions[0],
      sortSelect: SORT_OPTIONS[0],
    },
  })

  const {
    data: adSpots,
    loading,
    totalElements,
    paging,
    handleFilterChange,
  } = usePagination({
    getData: adSpotsApi.publicPaging,
    formValueSetter: setValue,
  })

  // need to add effect that will set value to select from searchparams

  const fMetaverse = searchParams.get('f__spot_type') || ''
  useEffect(() => {
    if (fMetaverse) setValue('metaverseSelect', { value: fMetaverse, label: fMetaverse.split('-')[0] })
  }, [])

  const sortBy = searchParams.get('d__sort') || ''
  useEffect(() => {
    if (sortBy)
      setValue(
        'sortSelect',
        //@ts-ignore
        SORT_OPTIONS.find((opt) => opt.value === sortBy)
      )
  }, [])

  const title =
    paging?.quick_search?.length || paging?.filters?.length
      ? adSpots?.length
        ? 'Found Ad Spaces'
        : 'Nothing found'
      : adSpots?.length
      ? 'Available Ad Spaces'
      : null

  return (
    <>
      <Outlet />
      <Header>
        <div className={s.wrapper}>
          <BurgerWrapper>
            <SelectControlled
              control={control}
              name="sortSelect"
              placeholder="Sort by..."
              options={SORT_OPTIONS}
              onChange={(opt) =>
                handleFilterChange({
                  fieldName: 'sortSelect',
                  searchParamName: 'd__sort',
                  option: opt,
                })
              }
              optionsIcons={SORT_ICON_BY_VALUE}
              icon={<SelectIcon />}
              className={s.sortSelect}
            />
            <SelectAsyncControlled
              control={control}
              name="usersSelect"
              firstOption={{ value: '', label: 'All Landowners' }}
              icon={<img src={metamaskIcon} alt="" style={{ width: '1.5rem' }} />}
              className={s.metaSelect}
              placeholder="All Landowners"
              onChange={(opt) =>
                handleFilterChange({
                  fieldName: 'usersSelect',
                  searchParamName: 'f__publisher_id',
                  option: opt,
                })
              }
              getData={publishersApi.publicPaging}
              customOptionsHandle={(data) =>
                data.map((user: Publisher) => ({
                  value: `${user.id}-Number-eq`,
                  label: `${user.name ? user.name : user.login}`,
                }))
              }
            />
            <SelectControlled
              control={control}
              name="metaverseSelect"
              className={s.metaSelect}
              placeholder="All Metaverses"
              onChange={(opt) =>
                handleFilterChange({
                  fieldName: 'metaverseSelect',
                  searchParamName: 'f__spot_type',
                  option: opt,
                })
              }
              optionsIcons={METAVERSE_IMAGE_BY_NAME_FILTER}
              options={filterMetaverseOptions}
              icon={<GlobeIcon />}
            />
            <Search paramPrefix="campaignAdSpots" className={s.search} />
          </BurgerWrapper>
        </div>
        <PageTitle title={title} count={totalElements} loading={loading} />
      </Header>
      <main className={s.cardTable}>
        {adSpots.length ? (
          adSpots.map((adSpot) => <AdSpotCard key={adSpot.id} adSpot={adSpot} />)
        ) : !paging?.quick_search?.length && !paging?.filters?.length && !loading ? (
          <NoContent icon={publisher} title="No Available Ad Spaces" subtitle="There is no active ad spaces now." />
        ) : null}
      </main>
    </>
  )
}
